package org.multiplatform.kotfolio.sections

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.models.Portfolio
import org.multiplatform.kotfolio.models.Section
import org.multiplatform.kotfolio.util.Constants
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.animation.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.multiplatform.kotfolio.rememberAppState

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_CARD_BG = Color.rgb(22, 18, 15)

val MarqueeKeyframes = Keyframes {
    0.percent {
        Modifier.translateX(0.percent)
    }
    100.percent {
        Modifier.styleModifier { property("transform", "translateX(-33.333%)") }
    }
}

@Composable
fun portfolioSectionV2() {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    var isMarqueePaused by remember { mutableStateOf(false) }
    var centeringOffset by remember { mutableStateOf(0.px) }
    var manualOffset by remember { mutableStateOf(0.px) }

    Column(
        modifier = Modifier
            .id(Section.Portfolio.id)
            .fillMaxWidth()
            .padding(top = if (isDesktop) 150.px else 80.px, bottom = 0.px)
            .styleModifier {
                // Multi-layered background:
                // 1. A bottom-to-top gradient that fades out the grid and background color
                // 2. The grid pattern itself
                // 3. The base background color
                property(
                    "background",
                    """
                    linear-gradient(to bottom, transparent 70%, rgb(44, 25, 16) 100%),
                    radial-gradient(circle at 2px 2px, rgba(255,255,255,0.01) 1px, transparent 0),
                    rgb(26, 10, 0)
                    """.trimIndent(),
                )
                property("background-size", "100% 100%, 40px 40px, 100% 100%")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Header
        // Header
        Column(
            modifier = Modifier.margin(bottom = 40.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Span(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(16.px)
                    .fontWeight(FontWeight.Bold)
                    .color(AMBER_500)
                    .margin(bottom = 12.px)
                    .styleModifier {
                        property("letter-spacing", "6px")
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) {
                Text("Portfolio")
            }

            P(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(if (isDesktop) 54.px else 36.px)
                    .fontWeight(FontWeight.Black)
                    .color(Colors.White)
                    .margin(topBottom = 0.px)
                    .textAlign(TextAlign.Center)
                    .toAttrs(),
            ) {
                Text("Project Involvements")
            }

            Row(
                modifier = Modifier.margin(top = 24.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                MarqueeNavButton(isLeft = true) {
                    manualOffset += 450.px
                }

                Box(
                    modifier = Modifier
                        .margin(leftRight = 32.px)
                        .width(100.px)
                        .height(3.px)
                        .backgroundColor(AMBER_500)
                        .borderRadius(2.px),
                )

                MarqueeNavButton(isLeft = false) {
                    manualOffset -= 450.px
                }
            }
        }

        // Marquee Container
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .overflow(Overflow.Hidden)
                .padding(topBottom = 80.px)
                .onMouseLeave {
                    isMarqueePaused = false
                    centeringOffset = 0.px
                }
                .styleModifier {
                    property("white-space", "nowrap")
                },
        ) {
            // Centering Layer
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("transform", "translateX(${(centeringOffset + manualOffset)})")
                        property("transition", "transform 1.2s cubic-bezier(0.22, 1, 0.36, 1)")
                    },
            ) {
                // Animated Row
                Row(
                    modifier = Modifier
                        .id("portfolio-marquee-row")
                        .fillMaxWidth()
                        .animation(
                            MarqueeKeyframes.toAnimation(
                                duration = 50.s,
                                timingFunction = AnimationTimingFunction.Linear,
                                iterationCount = AnimationIterationCount.Infinite,
                            ),
                        )
                        .styleModifier {
                            property("animation-play-state", if (isMarqueePaused) "paused" else "running")
                        },
                ) {
                    repeat(3) { instanceIndex ->
                        PortfolioList(instanceIndex = instanceIndex, onHover = { cardId ->
                            isMarqueePaused = true
                            manualOffset = 0.px

                            val row = document.getElementById("portfolio-marquee-row")
                            val card = document.getElementById(cardId)

                            if (row != null && card != null) {
                                val cardRect = card.getBoundingClientRect()
                                val viewportCenter = window.innerWidth / 2.0
                                val cardCenter = cardRect.left + (cardRect.width / 2.0)
                                val diff = viewportCenter - cardCenter
                                centeringOffset = diff.px
                            }
                        })
                    }
                }
            }
        }
    }
}

@Composable
private fun MarqueeNavButton(isLeft: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(80.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
            .borderRadius(50.percent)
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("pointer-events", "auto")
                property("backdrop-filter", "blur(10px)")
                property("transition", "all 0.3s ease")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .onMouseEnter {
                // Subtle amber glow on hover
            },
    ) {
        Image(
            src = if (isLeft) "chevron_left.png" else "chevron_right.png",
            modifier = Modifier.size(36.px).objectFit(ObjectFit.Contain),
        )
    }
}

@Composable
private fun PortfolioList(instanceIndex: Int, onHover: (String) -> Unit) {
    Portfolio.entries.forEach { project ->
        PortfolioCardV2(instanceIndex, project, onHover)
    }
}

@Composable
private fun PortfolioCardV2(
    instanceIndex: Int,
    project: Portfolio,
    onHover: (String) -> Unit,
) {
    var isHovered by remember { mutableStateOf(false) }
    val cardId = "portfolio-card-${project.title.replace(" ", "-")}-$instanceIndex"

    Box(
        modifier = Modifier
            .id(cardId)
            .margin(leftRight = 20.px)
            .width(if (project.isWeb) 480.px else 360.px) // Web projects get wider cards
            .minWidth(if (project.isWeb) 480.px else 360.px)
            .height(580.px)
            .backgroundColor(DARK_CARD_BG)
            .borderRadius(32.px)
            .border(1.px, LineStyle.Solid, if (isHovered) AMBER_500 else Color.rgba(245, 158, 11, 0.1f))
            .overflow(Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onClick {
                window.open(project.link, "_blank")
            }
            .onMouseEnter {
                isHovered = true
                onHover(cardId)
            }
            .onMouseLeave {
                isHovered = false
            }
            .styleModifier {
                property("flex-shrink", "0")
                property("box-shadow", if (isHovered) "0 30px 60px rgba(245, 158, 11, 0.25)" else "0 20px 40px rgba(0,0,0,0.3)")
                property("transform", if (isHovered) "scale(1.1) translateY(-10px)" else "scale(1) translateY(0)")
                property("transition", "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)")
                property("z-index", if (isHovered) "100" else "1")
                property("position", "relative")
            },
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            // Project Image with Gradient Overlay
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(300.px),
            ) {
                Image(
                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover).styleModifier {
                        property("transition", "transform 0.6s ease")
                        if (isHovered) property("transform", "scale(1.1)")
                    },
                    src = project.image,
                )

                // Fade overlay
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("background", "linear-gradient(to bottom, transparent 0%, $DARK_CARD_BG 95%)")
                        },
                )

                // Project Accent Line
                Box(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .margin(left = 32.px, bottom = 0.px)
                        .width(if (isHovered) 80.px else 40.px)
                        .height(3.px)
                        .backgroundColor(AMBER_500)
                        .borderRadius(2.px)
                        .styleModifier {
                            property("transition", "width 0.4s ease")
                        },
                )
            }

            // Project Info
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(all = 32.px),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    P(
                        attrs = Modifier
                            .fontFamily(Constants.FONT_FAMILY)
                            .fontSize(24.px)
                            .fontWeight(FontWeight.Black)
                            .color(Colors.White)
                            .margin(topBottom = 0.px)
                            .toAttrs(),
                    ) {
                        Text(project.title)
                    }

                    Box(
                        modifier = Modifier
                            .margin(left = 12.px)
                            .backgroundColor(Color.rgba(245, 158, 11, 0.1f))
                            .padding(leftRight = 8.px, topBottom = 2.px)
                            .borderRadius(4.px),
                    ) {
                        Span(
                            attrs = Modifier
                                .fontFamily(Constants.FONT_FAMILY)
                                .fontSize(10.px)
                                .fontWeight(FontWeight.Bold)
                                .color(AMBER_500)
                                .toAttrs(),
                        ) {
                            Text(project.language)
                        }
                    }
                }


                P(
                    attrs = Modifier
                        .margin(top = 16.px, bottom = 24.px)
                        .fontFamily(Constants.FONT_FAMILY)
                        .fontSize(14.px)
                        .color(Color.rgba(255, 255, 255, 0.6f))
                        .lineHeight(1.6)
                        .styleModifier {
                            property("white-space", "normal")
                            property("display", "-webkit-box")
                            property("-webkit-line-clamp", "3")
                            property("-webkit-box-orient", "vertical")
                            property("overflow", "hidden")
                        }
                        .toAttrs(),
                ) {
                    Text(project.description)
                }

                // Tech Tags
                //val projectTags = getTagsForProject(project.title)
                Row(
                    modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
                ) {
                    project.tagList.forEach { tag ->
                        Box(
                            modifier = Modifier
                                .margin(right = 8.px, bottom = 8.px)
                                .backgroundColor(Color.rgba(245, 158, 11, 0.05f))
                                .padding(leftRight = 10.px, topBottom = 5.px)
                                .borderRadius(6.px)
                                .border(1.px, LineStyle.Solid, Color.rgba(245, 158, 11, 0.2f)),
                        ) {
                            Span(
                                attrs = Modifier
                                    .fontFamily(Constants.FONT_FAMILY)
                                    .fontSize(10.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(AMBER_500)
                                    .toAttrs(),
                            ) {
                                Text(tag)
                            }
                        }
                    }
                }
            }
        }
    }
}

/*
private fun getTagsForProject(title: String): List<String> {
    return when (title) {
        "Fleet Drive Backoffice" -> listOf("KMP", "Compose HTML", "Kobweb", "Kotlin", "Real-time Tracking", "Data Viz")
        "AnimeLoom" -> listOf("Kotlin", "Jetpack Compose", "REST API", "MVVM")
        "Currenzy" -> listOf("Android SDK", "Java", "Retrofit")
        "SoloScape" -> listOf("Jetpack Compose", "Room DB", "Kotlin Coroutines")
        "Solofolio" -> listOf("Kotlin", "Compose Multiplatform", "Silk UI")
        "SoloEats" -> listOf("Android SDK", "XML", "Material Design")
        "SoloDaily" -> listOf("Kotlin", "Retrofit", "News API")
        "UnionBank Online" -> listOf("Java", "Agile", "Banking Systems")
        "McDelivery PH" -> listOf("Java", "REST API", "E-Commerce")
        else -> listOf("Android", "Kotlin")
    }
}
*/
